import { useApolloClient } from "@apollo/client";
import { fromPairs, get, isEmpty, isNil, isPlainObject, noop } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { findFieldByPath, FIELDS, groupLabel } from "../../data-fields";
import { unsettledZones } from "../../field-status";
import { PROPOSAL_STATUSES } from "./draft-content";
import { fieldFitsZone } from "./zones";

const POLL_DELAY = 2000;
const CACHE_PREFIX = "kayros.dynamic-documents.lucie";
const MAX_POLLS = 90;

const JOB_STATUSES = {
  RUNNING: "RUNNING",
  DONE: "DONE",
  FAILED: "FAILED",
};

export const LUCIE_OUTCOMES = {
  FOUND: "FOUND",
  NOTHING: "NOTHING",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
};

const lucieFields = () =>
  FIELDS.map(({ path, label, group }) => ({
    path,
    label,
    group: groupLabel(group),
  }));

const wait = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

export const mergeProposals = (content, proposals, targets = []) => {
  const zones = new Map(
    unsettledZones(content.anchors, content.anchorMappings).map((zone) => [
      zone.id,
      zone,
    ]),
  );
  const next = { ...content.proposals };
  for (const { zone: zoneId, path, reason } of proposals) {
    const zone = zones.get(zoneId);
    if (isNil(zone) || !fieldFitsZone(zone, findFieldByPath(path))) continue;
    const current = next[zoneId];
    if (get(current, "status") === PROPOSAL_STATUSES.ACCEPTED) continue;
    if (
      get(current, "status") === PROPOSAL_STATUSES.DISMISSED &&
      current.path === path
    )
      continue;
    next[zoneId] = {
      path,
      reason: reason ?? null,
      status: PROPOSAL_STATUSES.PENDING,
    };
  }
  for (const target of targets) {
    if (!zones.has(target.id) || !isNil(next[target.id])) continue;
    next[target.id] = {
      path: null,
      reason: null,
      status: PROPOSAL_STATUSES.NONE,
    };
  }
  return { ...content, proposals: next };
};

const cacheKey = ({ id }) => `${CACHE_PREFIX}.${id}`;

const readCache = ({ id }) => {
  try {
    const cached = JSON.parse(window.sessionStorage.getItem(cacheKey({ id })));
    return isPlainObject(cached) ? cached : {};
  } catch (_error) {
    return {};
  }
};

export const readCachedProposals = ({ template: { id }, zones }) => {
  const cached = readCache({ id });
  const covered = zones.filter((zone) => Array.isArray(cached[zone.id]));
  return {
    proposals: covered.flatMap((zone) => cached[zone.id]),
    covered,
  };
};

export const storeProposals = ({ template: { id }, zones, proposals }) => {
  try {
    window.sessionStorage.setItem(
      cacheKey({ id }),
      JSON.stringify({
        ...readCache({ id }),
        ...fromPairs(
          zones.map((zone) => [
            zone.id,
            proposals.filter((proposal) => proposal.zone === zone.id),
          ]),
        ),
      }),
    );
    return true;
  } catch (_error) {
    return false;
  }
};

export const zonesWithoutProposal = (content) =>
  unsettledZones(content.anchors, content.anchorMappings).filter((zone) =>
    isNil(content.proposals[zone.id]),
  );

const useLucie = ({ template: { id } }) => {
  const client = useApolloClient();
  const [startProposals] = useMutation(
    graphql.mutations.START_DOCUMENT_TEMPLATE_PROPOSALS,
    { onError: noop },
  );
  const [running, setRunning] = useState(false);
  const aliveRef = useRef(true);

  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    [],
  );

  const poll = async (jobId) => {
    for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
      await wait(POLL_DELAY);
      if (!aliveRef.current) return { outcome: LUCIE_OUTCOMES.CANCELLED };
      const job = await client
        .query({
          query: graphql.queries.DOCUMENT_TEMPLATE_PROPOSALS_JOB,
          variables: { where: { id: jobId } },
          fetchPolicy: "network-only",
        })
        .then((result) => get(result, "data.documentTemplateProposalsJob"))
        .catch(() => null);
      const status = get(job, "status");
      if (isNil(job) || status === JOB_STATUSES.FAILED)
        return { outcome: LUCIE_OUTCOMES.FAILED };
      if (status === JOB_STATUSES.DONE) {
        const proposals = get(job, "proposals") || [];
        return {
          outcome: isEmpty(proposals)
            ? LUCIE_OUTCOMES.NOTHING
            : LUCIE_OUTCOMES.FOUND,
          proposals,
        };
      }
    }
    return { outcome: LUCIE_OUTCOMES.FAILED };
  };

  const ask = async ({ zones }) => {
    if (isEmpty(zones)) return { outcome: LUCIE_OUTCOMES.NOTHING };
    setRunning(true);
    try {
      const started = await startProposals({
        variables: {
          where: { id },
          data: { zones: zones.map((zone) => zone.id), fields: lucieFields() },
        },
      });
      const jobId = get(started, "data.startDocumentTemplateProposals");
      if (isNil(jobId)) return { outcome: LUCIE_OUTCOMES.FAILED };
      const result = await poll(jobId);
      return result;
    } finally {
      if (aliveRef.current) setRunning(false);
    }
  };

  return { running, ask };
};

export default useLucie;
